import type { JsonObject, MobileUiInboundAction } from "./index.js";
import type { SessionLiveRuntimeEvent } from "./session-live.js";
import type { SessionGatewayAccessResponse } from "./sessions.js";

const GATEWAY_PROTOCOL_VERSION = 4;
const GATEWAY_REQUEST_TIMEOUT_MS = 15_000;
const GATEWAY_TURN_TIMEOUT_MS = 10 * 60_000;

type GatewayRequestFrame = {
  type: "req";
  id: string;
  method: string;
  params?: unknown;
};

type GatewayResponseFrame = {
  type: "res";
  id: string;
  ok: boolean;
  payload?: unknown;
  error?: {
    code?: string;
    message?: string;
    details?: unknown;
  };
};

type GatewayEventFrame = {
  type: "event";
  event: string;
  payload?: unknown;
  seq?: number;
};

type GatewayFrame = GatewayResponseFrame | GatewayEventFrame;

type PendingRequest = {
  resolve: (value: unknown) => void;
  reject: (error: Error) => void;
  timeout: ReturnType<typeof setTimeout>;
};

export type SessionGatewayClientInfo = {
  id: string;
  displayName: string;
  version: string;
  platform: string;
  mode: "webchat" | "mobile";
};

export type SessionGatewayCallbacks = {
  onRuntimeEvent?: (event: SessionLiveRuntimeEvent) => void;
  onSessionChanged?: () => void;
};

export type RunSessionGatewayTurnParams = SessionGatewayCallbacks & {
  access: SessionGatewayAccessResponse;
  text: string;
  action?: MobileUiInboundAction | null;
  thinkLevel?: string | null;
  signal?: AbortSignal;
  client: SessionGatewayClientInfo;
  createId?: () => string;
};

export type OpenSessionGatewaySubscriptionParams = SessionGatewayCallbacks & {
  access: SessionGatewayAccessResponse;
  signal: AbortSignal;
  client: SessionGatewayClientInfo;
  createId?: () => string;
};

export async function runSessionGatewayTurn(params: RunSessionGatewayTurnParams): Promise<void> {
  const text = params.text.trim();
  if (!text) {
    throw new Error("Message text is required.");
  }

  const socket = new WebSocket(params.access.gatewayWebSocketUrl);
  const pending = new Map<string, PendingRequest>();
  const createId = params.createId ?? browserRandomUuid;
  const runId = createId();
  let runtimeSeq = 0;
  let connected = false;
  let settled = false;
  let terminal: (() => void) | null = null;
  let failTerminal: ((error: Error) => void) | null = null;

  const cleanup = () => {
    params.signal?.removeEventListener("abort", abort);
    rejectPendingRequests(pending, "Runtime gateway connection closed.");
    closeSocket(socket);
  };

  const abort = () => {
    failTerminal?.(new Error("Runtime gateway turn was aborted."));
    cleanup();
  };

  params.signal?.addEventListener("abort", abort, { once: true });

  try {
    await new Promise<void>((resolve, reject) => {
      const timeout = setTimeout(
        () => reject(new Error("Runtime gateway turn timed out.")),
        GATEWAY_TURN_TIMEOUT_MS,
      );
      terminal = () => {
        if (settled) return;
        settled = true;
        clearTimeout(timeout);
        resolve();
      };
      failTerminal = (error) => {
        if (settled) return;
        settled = true;
        clearTimeout(timeout);
        reject(error);
      };

      socket.onmessage = (message) => {
        const frame = parseGatewayFrame(message.data);
        if (!frame) return;

        if (frame.type === "res") {
          settleGatewayResponse(pending, frame);
          return;
        }

        if (frame.event === "connect.challenge") {
          void connectGateway(socket, pending, params.access, params.client, createId)
            .then(async () => {
              connected = true;
              await requestGateway(
                socket,
                pending,
                "sessions.messages.subscribe",
                { key: params.access.sessionKey },
                createId,
              );
              await requestGateway(
                socket,
                pending,
                "chat.send",
                buildChatSendParams(params, runId),
                createId,
              );
            })
            .catch((error) =>
              failTerminal?.(error instanceof Error ? error : new Error(String(error))),
            );
          return;
        }

        if (!connected) return;

        routeGatewayEvent({
          frame,
          access: params.access,
          fallbackRunId: runId,
          nextSeq: () => ++runtimeSeq,
          onRuntimeEvent: (event) => {
            params.onRuntimeEvent?.(event);
            if (isTerminalRuntimeEvent(event)) {
              params.onSessionChanged?.();
              terminal?.();
            }
          },
          onSessionChanged: params.onSessionChanged,
        });
      };

      socket.onerror = () => {
        failTerminal?.(new Error("Runtime gateway websocket failed."));
      };

      socket.onclose = () => {
        if (!settled) {
          failTerminal?.(new Error("Runtime gateway websocket closed before the turn completed."));
        }
      };
    });
  } finally {
    cleanup();
  }
}

export async function openSessionGatewaySubscription(
  params: OpenSessionGatewaySubscriptionParams,
): Promise<void> {
  const socket = new WebSocket(params.access.gatewayWebSocketUrl);
  const pending = new Map<string, PendingRequest>();
  const createId = params.createId ?? browserRandomUuid;
  let runtimeSeq = 0;
  let connected = false;
  let settled = false;
  let failTerminal: ((error: Error) => void) | null = null;

  const cleanup = () => {
    params.signal.removeEventListener("abort", abort);
    rejectPendingRequests(pending, "Runtime gateway connection closed.");
    closeSocket(socket);
  };

  const abort = () => {
    settled = true;
    cleanup();
  };

  params.signal.addEventListener("abort", abort, { once: true });

  try {
    await new Promise<void>((resolve, reject) => {
      failTerminal = (error) => {
        if (settled) return;
        settled = true;
        reject(error);
      };

      socket.onmessage = (message) => {
        const frame = parseGatewayFrame(message.data);
        if (!frame) return;

        if (frame.type === "res") {
          settleGatewayResponse(pending, frame);
          return;
        }

        if (frame.event === "connect.challenge") {
          void connectGateway(socket, pending, params.access, params.client, createId)
            .then(async () => {
              connected = true;
              await requestGateway(
                socket,
                pending,
                "sessions.messages.subscribe",
                { key: params.access.sessionKey },
                createId,
              );
            })
            .catch((error) =>
              failTerminal?.(error instanceof Error ? error : new Error(String(error))),
            );
          return;
        }

        if (!connected) return;

        routeGatewayEvent({
          frame,
          access: params.access,
          fallbackRunId: "",
          nextSeq: () => ++runtimeSeq,
          onRuntimeEvent: params.onRuntimeEvent,
          onSessionChanged: params.onSessionChanged,
        });
      };

      socket.onerror = () => {
        failTerminal?.(new Error("Runtime gateway websocket failed."));
      };

      socket.onclose = () => {
        if (!settled) {
          settled = true;
          resolve();
        }
      };
    });
  } finally {
    cleanup();
  }
}

async function connectGateway(
  socket: WebSocket,
  pending: Map<string, PendingRequest>,
  access: SessionGatewayAccessResponse,
  client: SessionGatewayClientInfo,
  createId: () => string,
) {
  await requestGateway(
    socket,
    pending,
    "connect",
    {
      minProtocol: GATEWAY_PROTOCOL_VERSION,
      maxProtocol: GATEWAY_PROTOCOL_VERSION,
      client,
      caps: ["tool-events"],
      role: "operator",
      scopes: ["operator.read", "operator.write"],
      auth: {
        sessionToken: access.sessionToken,
      },
    },
    createId,
  );
}

function requestGateway(
  socket: WebSocket,
  pending: Map<string, PendingRequest>,
  method: string,
  params: unknown,
  createId: () => string,
): Promise<unknown> {
  if (socket.readyState !== WebSocket.OPEN) {
    return Promise.reject(new Error("Runtime gateway websocket is not open."));
  }

  const id = createId();
  const frame: GatewayRequestFrame = { type: "req", id, method, params };
  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      pending.delete(id);
      reject(new Error(`Runtime gateway request timed out: ${method}`));
    }, GATEWAY_REQUEST_TIMEOUT_MS);
    pending.set(id, { resolve, reject, timeout });
    socket.send(JSON.stringify(frame));
  });
}

function settleGatewayResponse(pending: Map<string, PendingRequest>, frame: GatewayResponseFrame) {
  const pendingRequest = pending.get(frame.id);
  if (!pendingRequest) return;

  pending.delete(frame.id);
  clearTimeout(pendingRequest.timeout);
  if (frame.ok) {
    pendingRequest.resolve(frame.payload);
    return;
  }

  pendingRequest.reject(new Error(frame.error?.message || "Runtime gateway request failed."));
}

function buildChatSendParams(params: RunSessionGatewayTurnParams, runId: string) {
  const runtimeProfile = params.access.runtimeProfile;
  const authProfileOverride = runtimeProfile.authProfileOverride?.trim();
  const thinkLevel = params.thinkLevel?.trim();

  return {
    sessionKey: params.access.sessionKey,
    message: params.text.trim(),
    idempotencyKey: runId,
    agentiq: {
      tenantId: params.access.tenantId,
      userId: params.access.userId,
      agentId: params.access.agentId,
      sessionId: params.access.sessionId,
      executionRequirements: params.access.executionRequirements,
      sentAt: new Date().toISOString(),
      providerOverride: runtimeProfile.providerOverride,
      modelOverride: runtimeProfile.modelOverride,
      ...(authProfileOverride ? { authProfileOverride } : {}),
      ...(params.action ? { action: params.action } : {}),
      ...(thinkLevel ? { thinkLevel } : {}),
    },
  };
}

function routeGatewayEvent(params: {
  frame: GatewayEventFrame;
  access: SessionGatewayAccessResponse;
  fallbackRunId: string;
  nextSeq: () => number;
  onRuntimeEvent?: (event: SessionLiveRuntimeEvent) => void;
  onSessionChanged?: () => void;
}) {
  if (params.frame.event === "sessions.changed" || params.frame.event === "session.message") {
    if (isGatewaySessionPayload(params.frame.payload, params.access.sessionKey)) {
      params.onSessionChanged?.();
    }
    return;
  }

  if (params.frame.event !== "agent" && params.frame.event !== "session.tool") {
    return;
  }

  const runtimeEvent = toRuntimeEvent({
    frame: params.frame,
    access: params.access,
    fallbackRunId: params.fallbackRunId,
    nextSeq: params.nextSeq,
  });
  if (runtimeEvent) {
    params.onRuntimeEvent?.(runtimeEvent);
  }
}

function parseGatewayFrame(raw: unknown): GatewayFrame | null {
  if (typeof raw !== "string") {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as GatewayFrame;
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

function isGatewaySessionPayload(payload: unknown, sessionKey: string): boolean {
  const record = asRecord(payload);
  if (!record) return false;
  return (
    readString(record.sessionKey) === sessionKey || readString(record.sessionId) === sessionKey
  );
}

function toRuntimeEvent(params: {
  frame: GatewayEventFrame;
  access: SessionGatewayAccessResponse;
  fallbackRunId: string;
  nextSeq: () => number;
}): SessionLiveRuntimeEvent | null {
  const payload = asRecord(params.frame.payload);
  if (!payload) return null;

  const sessionKey = readString(payload.sessionKey) ?? params.access.sessionKey;
  if (sessionKey !== params.access.sessionKey) return null;

  const runId = readString(payload.runId) ?? params.fallbackRunId;
  const stream =
    readString(payload.stream) ?? (params.frame.event === "session.tool" ? "tool" : null);
  if (!runId || !stream) return null;

  const data = asRecord(payload.data) ?? {};
  const seq = readNumber(payload.seq) ?? readNumber(params.frame.seq) ?? params.nextSeq();
  const ts = readNumber(payload.ts) ?? Date.now();
  return {
    type: "session.runtime",
    sessionId: params.access.sessionId,
    runId,
    seq,
    stream,
    ts,
    data: data as JsonObject,
  };
}

function isTerminalRuntimeEvent(event: SessionLiveRuntimeEvent): boolean {
  if (event.stream === "error") {
    return true;
  }
  if (event.stream !== "lifecycle") {
    return false;
  }
  const phase = typeof event.data.phase === "string" ? event.data.phase : undefined;
  return phase === "end" || phase === "error";
}

function rejectPendingRequests(pending: Map<string, PendingRequest>, message: string) {
  for (const request of pending.values()) {
    clearTimeout(request.timeout);
    request.reject(new Error(message));
  }
  pending.clear();
}

function closeSocket(socket: WebSocket) {
  if (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING) {
    socket.close();
  }
}

function browserRandomUuid(): string {
  const randomUuid = globalThis.crypto?.randomUUID?.();
  if (!randomUuid) {
    throw new Error("Runtime gateway client requires crypto.randomUUID.");
  }
  return randomUuid;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function readString(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function readNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}
